import { CHANNELS } from "../shared/channels";
import { ShareBoard, SharedChange, SharePerson } from "../shared/sharing";
import { call, listen } from "./bridge";
import { renderList } from "./dom";
import { t } from "./i18n";

// what other people share with me, for the views that show it read only next to my own

let board: Promise<ShareBoard> | null = null;
const watchers: Array<{ kind: string; run: () => void }> = [];

export function loadShareBoard(fresh = false): Promise<ShareBoard> {
  if (!board || fresh) {
    board = call<ShareBoard>(CHANNELS.sharesGet).catch(() => ({ kinds: [], people: [], mine: {}, withMe: [] }));
  }
  return board;
}

// the people sharing this kind with me
export async function sharersOf(kind: string): Promise<SharePerson[]> {
  // asked fresh each time, sharing can change from someone else's phone
  const current = await loadShareBoard(true);
  return current.withMe.filter((person) => person.kinds.includes(kind)).map(({ id, name }) => ({ id, name }));
}

// everyone sharing this kind with me and their things. one that fails just shows as empty
export async function readSharedAll<T>(kind: string, empty: T): Promise<Array<SharePerson & { data: T }>> {
  const people = await sharersOf(kind);
  return Promise.all(people.map(async (person) => ({
    ...person,
    data: await call<T>(CHANNELS.sharedRead, kind, person.id).catch(() => empty)
  })));
}

// runs when someone changes what they share with me, or changes something of this kind they share
export function onSharedChange(kind: string, run: () => void): void {
  watchers.push({ kind, run });
}

export function watchShares(): void {
  listen<{ viewer?: string }>(CHANNELS.sharesUpdated, () => {
    board = null;
    watchers.forEach((watcher) => watcher.run());
  });
  listen<SharedChange>(CHANNELS.sharedUpdated, (change) => {
    watchers.filter((watcher) => watcher.kind === change.kind).forEach((watcher) => watcher.run());
  });
}

// a small read only section header, "pauline's notes · read only"
export function sharedHead(name: string, what: string): HTMLElement {
  const head = document.createElement("div");
  head.className = "section-head";
  const title = document.createElement("h2");
  title.textContent = t("{name}'s {what} · read only", { name: name.toLowerCase(), what });
  head.append(title);
  return head;
}

// one read only list per person sharing with me, under my own
export function fillSharedBox<T>(box: HTMLElement, people: Array<SharePerson & { data: T[] }>, what: string, build: (item: T) => HTMLElement, emptyText: string): void {
  box.hidden = people.length === 0;
  box.replaceChildren(...people.map((person) => {
    const section = document.createElement("div");
    section.className = "stack shared-box";
    const list = document.createElement("ul");
    list.className = "card-list";
    renderList(list, person.data, build);
    section.append(sharedHead(person.name, what), list);
    if (!person.data.length) {
      const empty = document.createElement("p");
      empty.className = "empty";
      empty.textContent = emptyText;
      section.append(empty);
    }
    return section;
  }));
}
